import React from 'react';
import { useSelector } from 'react-redux';
import { twMerge } from 'tailwind-merge';

import {
  StockItem,
  StockItemContext,
  StockItemFormat,
} from '../../../../../common/types/StockItemTypes';
import { ContentType } from '../../../../Shared/enums';
import { selectShowAnimatedAiThumbnailButton } from '../../../selectors/searchSelectors';
import { AIEditButton } from './Buttons/AIEditButton';
import { DownloadButton } from './Buttons/DownloadButton';
import { FavoriteButton } from './Buttons/FavoriteButton';
import { MakerButton } from './Buttons/MakerButton';
import { RelatedVideosButton } from './Buttons/RelatedVideosButton';
import SameShootSameModelSimilarClipsDrawer from './SameShootSameModelSimilarClipsDrawer';

export interface VideoCardActionButtonsProps {
  isVertical: boolean;
  context: StockItemContext;
  isHovering: boolean;
  shouldShowAIEditButton: boolean;
  handleAIEditAction: () => void;
  containPoppers: boolean;
  stockItem: StockItem;
  stockItemFormats: StockItemFormat[];
  favoriteButtonTelemetryAction: () => void;
  openFoldersInNewWindow: boolean;
  handleClickAction: (e: React.MouseEvent<HTMLButtonElement>) => void;
  shouldShowMakerButton: boolean;
  makerButtonTelemetryAction: () => void;
  relatedVideosButtonRef: React.RefObject<HTMLButtonElement>;
  similarContentAction: () => void;
  shouldShowDownloadButton: boolean;
  downloadButtonTelemetryAction: () => void;
  onDownloadButtonClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  isContentEditPolling: boolean;
  isSimilarVideosDrawerOpen: boolean;
  drawerPortalContainer: HTMLElement | null;
  onDrawerMouseEnter?: () => void;
  onDrawerMouseLeave?: () => void;
  stockItemCardContainer: HTMLDivElement | null;
}

export const VideoCardActionButtons = ({
  isVertical,
  context,
  isHovering,
  shouldShowAIEditButton,
  handleAIEditAction,
  containPoppers,
  stockItem,
  stockItemFormats,
  favoriteButtonTelemetryAction,
  openFoldersInNewWindow,
  handleClickAction,
  shouldShowMakerButton,
  makerButtonTelemetryAction,
  relatedVideosButtonRef,
  similarContentAction,
  shouldShowDownloadButton,
  downloadButtonTelemetryAction,
  onDownloadButtonClick,
  isContentEditPolling,
  isSimilarVideosDrawerOpen,
  drawerPortalContainer,
  onDrawerMouseEnter,
  onDrawerMouseLeave,
  stockItemCardContainer,
}: VideoCardActionButtonsProps) => {
  const shouldShowRelatedVideosButton = context === StockItemContext.SEARCH;
  const showAnimatedAiThumbnailButton = useSelector(
    selectShowAnimatedAiThumbnailButton
  );

  // Shared subcomponents
  const relatedVideosDrawer = shouldShowRelatedVideosButton && (
    <SameShootSameModelSimilarClipsDrawer
      open={isSimilarVideosDrawerOpen}
      stockItemId={stockItem.id}
      contentType={stockItem.contentType as ContentType}
      onOpenChange={(open: boolean) => {
        if (!open) {
          // only update the state if the drawer will be closing
          similarContentAction();
        }
      }}
      elementToPointTo={stockItemCardContainer}
      trigger={
        <RelatedVideosButton
          ref={relatedVideosButtonRef}
          stockItem={stockItem}
          onClick={similarContentAction}
        />
      }
      portalContainer={drawerPortalContainer}
      onMouseEnter={onDrawerMouseEnter}
      onMouseLeave={onDrawerMouseLeave}
    />
  );

  const aiEditButton = shouldShowAIEditButton && (
    <AIEditButton
      onClick={handleAIEditAction}
      disabled={isContentEditPolling}
    />
  );

  const favoriteButton = (
    <FavoriteButton
      containPoppers={containPoppers}
      stockItem={stockItem}
      stockItemFormats={stockItemFormats}
      favoriteButtonTelemetryAction={favoriteButtonTelemetryAction}
      openFoldersInNewWindow={openFoldersInNewWindow}
      onClick={handleClickAction}
      context={context}
    />
  );

  const makerButton = shouldShowMakerButton &&
    !showAnimatedAiThumbnailButton && (
      <MakerButton
        stockItem={stockItem}
        makerButtonTelemetryAction={makerButtonTelemetryAction}
      />
    );

  const downloadButton = (
    <DownloadButton
      shouldShowDownloadButton={shouldShowDownloadButton}
      downloadButtonTelemetryAction={downloadButtonTelemetryAction}
      onClick={onDownloadButtonClick}
    />
  );

  if (isVertical) {
    return (
      <div
        className={twMerge(
          'flex flex-col w-full absolute items-end left-0 bottom-0 px-4 pb-4 gap-y-2 pointer-events-none',
          !isHovering && 'hidden'
        )}
        data-testid="action-buttons-vertical"
      >
        {aiEditButton && (
          <div className="pointer-events-auto">{aiEditButton}</div>
        )}
        <div className="pointer-events-auto">{favoriteButton}</div>
        {makerButton && (
          <div className="pointer-events-auto">{makerButton}</div>
        )}
        <div
          className={twMerge(
            shouldShowRelatedVideosButton ? 'w-full' : 'w-auto',
            'flex justify-between gap-y-2 pointer-events-auto'
          )}
        >
          {relatedVideosDrawer}
          {downloadButton}
        </div>
      </div>
    );
  }
  return (
    <div
      className={twMerge(
        'absolute flex items-center justify-between w-full left-0 bottom-0 px-4 pb-4 space-x-2',
        !isHovering && 'hidden'
      )}
      data-testid="action-buttons"
    >
      <div className="flex space-x-2">
        {showAnimatedAiThumbnailButton ? (
          <>
            {aiEditButton}
            {relatedVideosDrawer}
          </>
        ) : (
          <>
            {relatedVideosDrawer}
            {aiEditButton}
          </>
        )}

        {favoriteButton}
        {makerButton}
      </div>
      {downloadButton}
    </div>
  );
};
